import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useWallet } from '../contexts/WalletContext'
import { Upload as UploadIcon, Image, Video, FileText, DollarSign, Lock } from 'lucide-react'

export default function Upload() {
  const navigate = useNavigate()
  const { isConnected, address } = useWallet()
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    price: '',
    isPaid: false,
    isPrivate: false,
    category: 'general'
  })
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation
    const newErrors: Record<string, string> = {}
    
    if (!formData.title) {
      newErrors.title = 'Title is required'
    }
    
    if (!selectedFile) {
      newErrors.file = 'Please select a file to upload'
    }
    
    if (formData.isPaid && !formData.price) {
      newErrors.price = 'Price is required for paid content'
    }
    
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors)
      return
    }

    setLoading(true)
    try {
      console.log('Starting upload process...')
      console.log('User address:', address)
      console.log('Is connected:', isConnected)
      
      if (!isConnected || !address) {
        setErrors({ submit: 'Please connect your wallet first.' })
        setLoading(false)
        return
      }

      // Create content data object
      const contentData = {
        id: `content_${Date.now()}`,
        title: formData.title,
        description: formData.description,
        category: formData.category,
        isPaid: formData.isPaid,
        price: formData.isPaid ? parseFloat(formData.price) : 0,
        isPrivate: formData.isPrivate,
        createdAt: new Date().toISOString(),
        file: {
          name: selectedFile!.name,
          size: selectedFile!.size,
          type: selectedFile!.type
        }
      }

      // Convert file to data URL for storage
      console.log('Converting file to data URL...')
      const fileDataUrl = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader()
        reader.onload = () => {
          console.log('File converted successfully')
          resolve(reader.result as string)
        }
        reader.onerror = () => {
          console.error('Error reading file')
          reject(new Error('Failed to read file'))
        }
        reader.readAsDataURL(selectedFile!)
      })

      // Get user's profile handle
      let userProfileHandle = localStorage.getItem(`userProfile_${address}`)
      console.log('Looking for profile handle for address:', address)
      console.log('Found profile handle:', userProfileHandle)
      
      if (!userProfileHandle) {
        console.log('No profile found, using temporary handle for testing')
        // For testing purposes, use a temporary handle
        const tempHandle = `temp_${address?.slice(0, 8)}`
        console.log('Using temporary handle:', tempHandle)
        
        // Save a temporary profile entry
        localStorage.setItem(`userProfile_${address}`, tempHandle)
        
        const existingProfiles = JSON.parse(localStorage.getItem('harmonyProfiles') || '{}')
        existingProfiles[tempHandle] = {
          handle: tempHandle,
          displayName: 'Temporary User',
          bio: 'This is a temporary profile for testing content upload.',
          walletAddress: address,
          createdAt: new Date().toISOString(),
          isVerified: false,
          links: { x: '', instagram: '', discord: '', website: '' },
          nsfw: false
        }
        localStorage.setItem('harmonyProfiles', JSON.stringify(existingProfiles))
        
        userProfileHandle = tempHandle
      }

      // Save content to localStorage
      const existingContent = JSON.parse(localStorage.getItem('harmonyContent') || '{}')
      if (!existingContent[userProfileHandle]) {
        existingContent[userProfileHandle] = []
      }
      
      // Add file data URL to content
      const finalContentData = {
        ...contentData,
        fileDataUrl
      }
      
      existingContent[userProfileHandle].push(finalContentData)
      localStorage.setItem('harmonyContent', JSON.stringify(existingContent))

      console.log('Content uploaded successfully:', finalContentData)
      console.log('All content for user:', existingContent[userProfileHandle])
      
      // Simulate upload process
      await new Promise(resolve => setTimeout(resolve, 1500))
      
      // Show success message
      alert(`🎉 Content "${formData.title}" uploaded successfully!`)
      
      // Reset form
      setFormData({
        title: '',
        description: '',
        price: '',
        isPaid: false,
        isPrivate: false,
        category: 'general'
      })
      setSelectedFile(null)
      setErrors({})
      
      // Navigate to user's profile
      navigate(`/u/${userProfileHandle}`)
    } catch (error) {
      console.error('Error uploading content:', error)
      setErrors({ submit: 'Failed to upload content. Please try again.' })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }))
    
    // Clear error when user starts typing
    if (errors[field]) {
      setErrors(prev => ({
        ...prev,
        [field]: ''
      }))
    }
  }

  const getFileIcon = (file: File) => {
    if (file.type.startsWith('image/')) {
      return <Image className="w-8 h-8 text-blue-500" />
    } else if (file.type.startsWith('video/')) {
      return <Video className="w-8 h-8 text-purple-500" />
    } else {
      return <FileText className="w-8 h-8 text-gray-500" />
    }
  }

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
  }

  // Test function to debug upload issues
  const testUpload = async () => {
    console.log('=== UPLOAD DEBUG INFO ===')
    console.log('Address:', address)
    console.log('Is Connected:', isConnected)
    console.log('Selected File:', selectedFile)
    console.log('Form Data:', formData)
    console.log('Errors:', errors)
    
    // Check localStorage
    const allProfiles = localStorage.getItem('harmonyProfiles')
    const allContent = localStorage.getItem('harmonyContent')
    console.log('All Profiles:', allProfiles)
    console.log('All Content:', allContent)
    
    if (address) {
      const userProfile = localStorage.getItem(`userProfile_${address}`)
      console.log('User Profile Handle:', userProfile)
    }
    console.log('=== END DEBUG INFO ===')
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen flex items-center justify-center relative overflow-hidden"
           style={{
             background: `
               linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
               radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
               radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
             `,
             backgroundSize: '100% 100%, 800px 800px, 900px 900px'
           }}>
        {/* Metallic shine effect */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
        </div>
        
        <div className="text-center relative z-10">
          <UploadIcon className="w-16 h-16 text-gold-400 mx-auto mb-4" />
          <h1 className="text-3xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
              Wallet Required
            </span>
          </h1>
          <p className="text-purple-100 mb-8 text-lg">Please connect your wallet to upload content.</p>
          <button className="px-8 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200">
            Connect Wallet
          </button>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen relative overflow-hidden"
         style={{
           background: `
             linear-gradient(135deg, #1F2937 0%, #374151 25%, #4B5563 50%, #374151 75%, #1F2937 100%),
             radial-gradient(circle at 20% 30%, rgba(212, 175, 55, 0.1) 0%, transparent 50%),
             radial-gradient(circle at 80% 70%, rgba(138, 43, 226, 0.1) 0%, transparent 50%)
           `,
           backgroundSize: '100% 100%, 800px 800px, 900px 900px'
         }}>
      {/* Metallic shine effect */}
      <div className="absolute inset-0 opacity-10">
        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/8 to-transparent transform -skew-y-1 animate-pulse"></div>
      </div>
      
      <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12 relative">
        <div className="text-center mb-8">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Georgia, serif' }}>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-gold-300 via-gold-400 to-gold-500">
              Upload Content
            </span>
          </h1>
          <p className="text-purple-100 text-lg">
            Share your content with the HarmonyDataHub community.
          </p>
        </div>

        <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8 shadow-2xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* File Upload */}
            <div>
              <label className="block text-sm font-medium text-white mb-2">
                Content File <span className="text-red-400">*</span>
              </label>
              <div className="border-2 border-dashed border-white/30 rounded-lg p-6 text-center hover:border-gold-400 transition-colors bg-white/5">
                <input
                  type="file"
                  onChange={handleFileSelect}
                  className="hidden"
                  id="file-upload"
                  accept="image/*,video/*,.pdf,.doc,.docx,.txt"
                />
                <label htmlFor="file-upload" className="cursor-pointer">
                  {selectedFile ? (
                    <div className="flex items-center justify-center space-x-3">
                      {getFileIcon(selectedFile)}
                      <div className="text-left">
                        <p className="text-sm font-medium text-white">{selectedFile.name}</p>
                        <p className="text-xs text-purple-200">{formatFileSize(selectedFile.size)}</p>
                      </div>
                    </div>
                  ) : (
                      <div>
                        <UploadIcon className="w-12 h-12 text-gold-400 mx-auto mb-4" />
                        <p className="text-sm text-purple-100">
                          Click to upload or drag and drop
                        </p>
                        <p className="text-xs text-purple-200 mt-1">
                          Images, videos, documents (max 100MB)
                        </p>
                      </div>
                  )}
                </label>
              </div>
              {errors.file && <p className="text-red-500 text-sm mt-1">{errors.file}</p>}
            </div>

            {/* Title */}
            <div>
              <label htmlFor="title" className="block text-sm font-medium text-white mb-2">
                Title <span className="text-red-400">*</span>
              </label>
              <input
                type="text"
                id="title"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent ${errors.title ? 'border-red-400' : ''}`}
                placeholder="Enter content title"
              />
              {errors.title && <p className="text-red-400 text-sm mt-1">{errors.title}</p>}
            </div>

            {/* Description */}
            <div>
              <label htmlFor="description" className="block text-sm font-medium text-white mb-2">
                Description
              </label>
              <textarea
                id="description"
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                rows={4}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent"
                placeholder="Describe your content..."
              />
            </div>

            {/* Category */}
            <div>
              <label htmlFor="category" className="block text-sm font-medium text-white mb-2">
                Category
              </label>
              <select
                id="category"
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:ring-2 focus:ring-gold-400 focus:border-transparent"
              >
                <option value="general" className="bg-gray-800 text-white">General</option>
                <option value="art" className="bg-gray-800 text-white">Art</option>
                <option value="music" className="bg-gray-800 text-white">Music</option>
                <option value="photography" className="bg-gray-800 text-white">Photography</option>
                <option value="writing" className="bg-gray-800 text-white">Writing</option>
                <option value="education" className="bg-gray-800 text-white">Education</option>
                <option value="entertainment" className="bg-gray-800 text-white">Entertainment</option>
                <option value="other" className="bg-gray-800 text-white">Other</option>
              </select>
            </div>

            {/* Pricing Options */}
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPaid"
                  checked={formData.isPaid}
                  onChange={(e) => handleInputChange('isPaid', e.target.checked)}
                  className="h-4 w-4 text-gold-600 focus:ring-gold-500 border-white/30 rounded bg-white/10"
                />
                <label htmlFor="isPaid" className="ml-2 block text-sm text-white flex items-center">
                  <DollarSign className="w-4 h-4 mr-1 text-gold-400" />
                  Make this content paid
                </label>
              </div>

              {formData.isPaid && (
                <div>
                  <label htmlFor="price" className="block text-sm font-medium text-white mb-2">
                    Price (SUI) <span className="text-red-400">*</span>
                  </label>
                  <div className="relative">
                    <input
                      type="number"
                      id="price"
                      value={formData.price}
                      onChange={(e) => handleInputChange('price', e.target.value)}
                      className={`w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-purple-200 focus:ring-2 focus:ring-gold-400 focus:border-transparent pr-12 ${errors.price ? 'border-red-400' : ''}`}
                      placeholder="0.00"
                      step="0.01"
                      min="0"
                    />
                    <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-purple-200 text-sm">
                      SUI
                    </span>
                  </div>
                  {errors.price && <p className="text-red-400 text-sm mt-1">{errors.price}</p>}
                </div>
              )}
            </div>

            {/* Privacy Options */}
            <div className="space-y-4">
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isPrivate"
                  checked={formData.isPrivate}
                  onChange={(e) => handleInputChange('isPrivate', e.target.checked)}
                  className="h-4 w-4 text-gold-600 focus:ring-gold-500 border-white/30 rounded bg-white/10"
                />
                <label htmlFor="isPrivate" className="ml-2 block text-sm text-white flex items-center">
                  <Lock className="w-4 h-4 mr-1 text-gold-400" />
                  Make this content private (subscribers only)
                </label>
              </div>
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="bg-red-500/20 border border-red-400/30 rounded-lg p-4">
                <p className="text-red-400 text-sm">{errors.submit}</p>
              </div>
            )}

            {/* Debug Button */}
            <button
              type="button"
              onClick={testUpload}
              className="w-full px-4 py-2 bg-purple-500/20 border border-purple-400/30 text-purple-300 rounded-lg hover:bg-purple-500/30 transition-colors text-sm mb-4"
            >
              🔍 Debug Upload Info (Check Console)
            </button>

            {/* Submit Button */}
            <div className="flex gap-4">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="flex-1 px-6 py-3 border border-white/30 text-white rounded-lg hover:bg-white/10 transition-colors duration-200"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 flex items-center justify-center px-6 py-3 bg-gold-500 text-black font-semibold rounded-lg hover:bg-gold-600 transition-colors duration-200 disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-black mr-2"></div>
                    Uploading...
                  </>
                ) : (
                  <>
                    <UploadIcon className="w-4 h-4 mr-2" />
                    Upload Content
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Upload Info */}
        <div className="mt-6 p-6 bg-white/5 backdrop-blur-sm border border-white/20 rounded-lg">
          <h3 className="text-lg font-medium text-white mb-3">Upload Information</h3>
          <ul className="text-sm text-purple-100 space-y-2">
            <li className="flex items-center">
              <span className="w-2 h-2 bg-gold-400 rounded-full mr-3"></span>
              Files are encrypted and stored on Walrus decentralized storage
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-gold-400 rounded-full mr-3"></span>
              Content metadata is stored on Sui blockchain
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-gold-400 rounded-full mr-3"></span>
              You retain full ownership and control of your content
            </li>
            <li className="flex items-center">
              <span className="w-2 h-2 bg-gold-400 rounded-full mr-3"></span>
              Maximum file size: 100MB
            </li>
          </ul>
        </div>
      </div>
    </div>
  )
}
